import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { omit } from 'lodash';

import Api, { FailedRequest } from '@common/services/api';
import { useAppSelector } from '@common/hooks';
import { EStatus } from '@common/definitions';
import { selected } from '@modules/organisation/selectors/organisation';
import { ApiResponse } from '@common/services/api/types';
import { ShortcutTypes } from '@modules/admin/definitions';
import { Document, DocumentFolder, File } from '@modules/documents/types';

import ModalContent from '@common/components/modal/modal-content';
import ModalWindow from '@common/components/modal/modal-window';
import { AsyncList, AsyncDataProps } from '@common/components/list';
import SearchBar from '@common/components/search-bar';
import ShowMoreComponent from '@common/components/list-show-more';
import Placeholder from '@common/components/placeholder';
import Icon from '@common/components/icon';
import * as Alert from '@common/services/alert';
import DocumentListItem from './document-list-item';

import './add-document-shortcut.scss';

type Filter = {
  q: string | null;
  folderId?: string;
};

type OwnProps = {
  onAdd?: (newShortcut: any) => void;
  onClose?: () => void;
  show: boolean;
};

const AddDocumentShortcut = ({ onAdd, onClose, show }: OwnProps) => {
  const { t } = useTranslation();
  const orgId = useAppSelector(selected).id;

  const [search, setSearch] = useState<string>('');
  const [navigationPath, setNavigationPath] = useState<DocumentFolder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<DocumentFolder | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const currentPath = useMemo(() => {
    if (navigationPath.length) {
      return navigationPath[navigationPath.length - 1];
    }
    return null;
  }, [navigationPath]);

  const onHide = useCallback(() => {
    setSearch('');
    setLoading(false);
    setSelectedFile(null);
    setSelectedFolder(null);
    setNavigationPath([]);
    if (onClose) onClose();
  }, [setSearch, setLoading, setSelectedFile, setSelectedFolder, setNavigationPath, onClose]);

  const onConfirm = useCallback(async () => {
    setLoading(true);

    const payload = {
      type: ShortcutTypes.DOCUMENT,
      target_id: selectedFile?.id || selectedFolder?.id,
    };

    try {
      const { data } = await Api.post<ApiResponse<Document>>(`/v3/organisations/${orgId}/shortcuts`, payload);
      if (onAdd) onAdd(data);
      Alert.success(t('organisation:forms_shortcut_create_success', {
        name: data.name,
      }));
      onHide();
    } catch (e) {
      Alert.forStatus((e as FailedRequest).status_code, {
        warning: t('organisation:forms_shortcut_create_warning'),
        error: t('organisation:forms_shortcut_create_error'),
      });
      setLoading(false);
      throw e;
    }
  }, [onHide, onAdd, setLoading, t, orgId, selectedFolder, selectedFile]);

  const navigateToParentFolder = useCallback(() => {
    setSelectedFile(null);
    setSelectedFolder(null);

    const newSelectedFolders = [...navigationPath];
    newSelectedFolders.pop();
    setNavigationPath(newSelectedFolders);
  }, [navigationPath, setNavigationPath, setSelectedFolder, setSelectedFile]);

  const title = useMemo(() => (
    <div className="AddDocumentShortcutModal__Title">
      {currentPath && (
        <Icon
          type="chevron_left"
          role="button"
          onClick={navigateToParentFolder}
        />
      )}
      <span>{currentPath?.name || t('organisation:settings_shortcuts_add_document')}</span>
    </div>
  ), [currentPath, navigateToParentFolder, t]);

  const confirmButtonText = useMemo(() => {
    if (selectedFile) return t('organisation:settings_shortcuts_add_document_file');
    return t('organisation:settings_shortcuts_add_document_folder');
  }, [selectedFile, t]);

  let endpoint = `/v1/organisations/${orgId}/documents`;
  if (currentPath && !search) endpoint += `/folders/${currentPath.id}`;
  if (search) endpoint += '/search';

  const data: AsyncDataProps<Document, Document, Filter> = {
    useCursor: true,
    onFetch: (nextCursor, filter, limit = 20) => {
      const query = Api.utils.toQuery({
        limit,
        cursor: nextCursor || true,
        status: EStatus.LIVE,
        ...omit(filter, ['folderId']),
      });
      return Api.get(`${endpoint}?${query}`);
    },
    filter: {
      folderId: currentPath?.id,
      q: search,
    },
  };

  return (
    <ModalWindow
      className="AddDocumentShortcutModal"
      show={show}
      onHide={onHide}
      size="small"
    >
      <ModalContent
        tooltip={(!currentPath && t('organisation:settings_shortcuts_add_document_description')) || undefined}
        title={title}
        onHide={onHide}
        onConfirm={onConfirm}
        confirmButtonDisabled={!selectedFolder && !selectedFile}
        confirmButtonLoading={loading}
        confirmButtonText={confirmButtonText}
        fullWidthContent
      >
        <SearchBar
          placeholder={t('common:form_filter_search_placeholder')}
          onSearch={(q) => {
            setSearch(q || '');
            setNavigationPath([]);
          }}
        />
        <AsyncList
          data={data}
          renderRow={DocumentListItem}
          rowProps={{
            selectedFile,
            selectedFolder,
            setSelectedFile,
            setSelectedFolder,
            setNavigationPath,
            canNavigate: search === '',
          }}
          placeholder={(
            <Placeholder
              title={t('documents:empty_folder')}
              size="small"
            />
          )}
          ShowMoreComponent={ShowMoreComponent}
        />
      </ModalContent>
    </ModalWindow>
  );
};

export default AddDocumentShortcut;
